import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../services/api';
import { toast } from 'sonner';
import { User, MapPin, Save, AlertCircle } from 'lucide-react';

const CoachProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({
    specialization: '',
    experience_years: '',
    bio: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip_code: ''
    },
    emergency_contact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [profileRes, userRes] = await Promise.all([
        api.get('/coach/profile'),
        api.get('/auth/me')
      ]);
      const data = profileRes.data;
      setUser(userRes.data);
      setProfile({
        specialization: data.specialization || '',
        experience_years: data.experience_years || '',
        bio: data.bio || '',
        address: {
          street: data.address?.street || '',
          city: data.address?.city || '',
          state: data.address?.state || '',
          zip_code: data.address?.zip_code || ''
        },
        emergency_contact: {
          name: data.emergency_contact?.name || '',
          phone: data.emergency_contact?.phone || '',
          relationship: data.emergency_contact?.relationship || ''
        }
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/coach/profile', {
        specialization: profile.specialization || null,
        experience_years: profile.experience_years ? parseInt(profile.experience_years) : null,
        bio: profile.bio || null,
        address: profile.address,
        emergency_contact: profile.emergency_contact
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const updateAddress = (field, value) => {
    setProfile(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const updateEmergencyContact = (field, value) => {
    setProfile(prev => ({
      ...prev,
      emergency_contact: { ...prev.emergency_contact, [field]: value }
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Profile">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="coach-profile-page">
        <form onSubmit={handleSave}>
          {/* Professional Info */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-500" />
                Professional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-500">Login Email (Contact Admin to change)</Label>
                <p className="font-medium text-slate-700">{user?.email || 'Not available'}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Specialization</Label>
                  <Input
                    value={profile.specialization}
                    onChange={(e) => setProfile({ ...profile, specialization: e.target.value })}
                    placeholder="e.g., Batting Coach, Fast Bowling"
                    data-testid="specialization-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input
                    type="number"
                    min="0"
                    value={profile.experience_years}
                    onChange={(e) => setProfile({ ...profile, experience_years: e.target.value })}
                    placeholder="e.g., 5"
                    data-testid="experience-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="Tell us about your coaching experience and philosophy..."
                  rows={3}
                  data-testid="bio-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-500" />
                Address Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="space-y-2">
                <Label>Street Address</Label>
                <Input
                  value={profile.address.street}
                  onChange={(e) => updateAddress('street', e.target.value)}
                  placeholder="123 Main Street, Apt 4B"
                  data-testid="street-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={profile.address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    placeholder="Edison"
                    data-testid="city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={profile.address.state}
                    onChange={(e) => updateAddress('state', e.target.value)}
                    placeholder="NJ"
                    data-testid="state-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={profile.address.zip_code}
                    onChange={(e) => updateAddress('zip_code', e.target.value)}
                    placeholder="08844"
                    data-testid="zip-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-rose-500" />
                Emergency Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={profile.emergency_contact.name}
                    onChange={(e) => updateEmergencyContact('name', e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="emergency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={profile.emergency_contact.phone}
                    onChange={(e) => updateEmergencyContact('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="emergency-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Relationship</Label>
                  <Input
                    value={profile.emergency_contact.relationship}
                    onChange={(e) => updateEmergencyContact('relationship', e.target.value)}
                    placeholder="Spouse, Parent, etc."
                    data-testid="emergency-relationship-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CoachProfile;
